// © Microsoft Corporation. All rights reserved.

import { ChildExperienceConfigInfo } from "@msnews/core";

/**
 * Information on a pivot selection.
 */
export interface PivotSelectionInfo {
    /** The ID of the pivot that was selected. This may or may not be in the PivotsNav list. */
    pivotId: string;

    /** The pagename of this pivot to be used for telemetry purposes */
    telemetryPageName?: string;

    /** The category of this pivot for telemetry purposes. Reported as the "pagepivot" value. */
    telemetryPivotCategory?: string;

    /** The display string for the pivot. */
    display?: string;

    /** Pivot category key */
    categoryKey?: string;

    /** Pivot subCategory key */
    subCategoryKey?: string;

    /** Page title to show up on pivot change */
    pageTitle?: string;
}

/**
 * Information on a pivot switch event.
 */
export interface PivotSwitchInfo {
    /** The old selected pivot state, or undefined if no pivot was previously selected. */
    oldSelection?: PivotSelectionInfo;

    /** The new selected pivot state. */
    newSelection: PivotSelectionInfo;
}

/**
 * Interface for route
 */
export interface PeregrineRoute {
    /** Route path */
    path?: string;

    /** Display string for route */
    display?: string;

    /** External URL */
    externalUrl?: string;

    /** Config index href for corresponding experience */
    experienceInfo?: ChildExperienceConfigInfo;

    /** Hrefs for experience config indexes for specific query params, e.g. layout preference */
    queryParamOverrides?: { [ queryParamKey: string ]: { [ queryParamValue: string ]: ChildExperienceConfigInfo } }

    /** Context for initial props for corresponding experience */
    context?: any;

    /** Id for the route, aka pivot id */
    pivotId?: string;

    /** Dynamic route/pivot source */
    dynamicPivotSource?: DynamicPivotsSourceType;

    /** Pivot category key */
    categoryKey?: string;

    /** Pivot subCategory key */
    subCategoryKey?: string;

    /** Page title to show up on pivot change */
    pageTitle?: string;
}

/**
 * List of helpers available for Dynamic Pivots
 */
export enum DynamicPivotsSourceType {
    NavigationData = "NavigationDataAdapter",
    TopicsData = "TopicsDataAdapter"
}
